/**
 * Local state machine that detects an update that "won't take" — the user keeps
 * reloading to update but the new build never activates (stuck service worker /
 * cache). An attempt timestamp is recorded right before each forced update (C.6
 * wires the actual `updateServiceWorker(true)` call); when too many attempts
 * pile up inside a short window we surface <UpdateRecoveryScreen/> instead of
 * looping forever. A healthy post-reload response clears the history.
 */

const STORAGE_KEY = 'fuelops.update_attempts.v1';
const RECOVERY_WINDOW_MS = 2 * 60 * 1000; // 2 minutes
const RECOVERY_THRESHOLD = 3;

const readTimestamps = (): number[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as unknown;

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(
      (value): value is number => typeof value === 'number' && Number.isFinite(value)
    );
  } catch {
    return [];
  }
};

const writeTimestamps = (timestamps: number[]): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(timestamps));
  } catch {
    // Quota/unavailable storage must not break the update flow.
  }
};

const withinWindow = (timestamps: number[], now: number): number[] =>
  timestamps.filter((timestamp) => now - timestamp <= RECOVERY_WINDOW_MS);

/** Records an update attempt at `now`, pruning timestamps outside the window. Returns the kept set. */
export const recordUpdateAttempt = (now: number = Date.now()): number[] => {
  const next = [...withinWindow(readTimestamps(), now), now];
  writeTimestamps(next);
  return next;
};

/** Clears the attempt history — call after a healthy post-reload 2xx. */
export const resetUpdateAttempts = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // non-fatal
  }
};

/** `true` once enough attempts happened inside the window to warrant the recovery screen. */
export const shouldShowRecovery = (now: number = Date.now()): boolean =>
  withinWindow(readTimestamps(), now).length >= RECOVERY_THRESHOLD;

export const UPDATE_ATTEMPTS_STORAGE_KEY = STORAGE_KEY;
export const UPDATE_RECOVERY_THRESHOLD = RECOVERY_THRESHOLD;
export const UPDATE_RECOVERY_WINDOW_MS = RECOVERY_WINDOW_MS;
